import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { API_SETTINGS } from '../api-services/API_SETTINGS';
import { BatchUploadModalPage } from '../modals/batch-upload-modal/batch-upload-modal.page';
import { BatchUpload } from '../models/upload/BatchUpload';
import { CryptoService } from './crypto.service';
import { StorageService } from './storageService.service';

const apiUrl = API_SETTINGS.API_URL + 'file';

@Injectable({
  providedIn: 'root'
})
export class BatchUploadService {

  public batchUpload: BatchUpload;
  public modalTitle: string;
  public modalDescription: string;

  private _isModalOpen: boolean = false;

  constructor(
    private httpClient: HttpClient,
    public cryptoService: CryptoService,
    public modalController: ModalController,
    public storageService: StorageService
  ) {
  }

  Init(file: any, fileType: string, projectId: string, compressFile: boolean, name: string = "", title: string = "", description: string = "") {
    this.batchUpload = new BatchUpload();
    this.batchUpload.File = file;
    this.batchUpload.FileType = fileType;
    this.batchUpload.ProjectId = projectId;
    this.batchUpload.CompressFile = compressFile;

    if(name && name.trim().length > 0){
      this.batchUpload.Name = name;
    }else{
      this.batchUpload.Name = file.name;
    }

    if(title && title.trim().length > 0){
      this.modalTitle = title;
    }else{
      this.modalTitle = "File " + file.name;
    }

    if(description && description.trim().length > 0){
      this.modalDescription = description;
    }

    this.openBatchUploadModal();
    this.batchUpload.CurrentUploadToken = this.CreateUploadToken();
    this.batchUpload.CurrentUploadCount = 0;
    this.batchUpload.TotalSize = this.batchUpload.File.size;

    this.batchUpload.TotalSizeDetails = this.GetSizeDetails(this.batchUpload.TotalSize);
    this.batchUpload.ReadableTotalSize = this.batchUpload.TotalSizeDetails.size + this.batchUpload.TotalSizeDetails.type;
    this.batchUpload.ReadableUploaded = "0MB";
    this.batchUpload.ProgressPercent = "0%";

    this.batchUpload.TotalUploadCounts = this.batchUpload.TotalSize % this.batchUpload.ByteChunkSize === 0 ? this.batchUpload.TotalSize / this.batchUpload.ByteChunkSize : Math.floor(this.batchUpload.TotalSize / this.batchUpload.ByteChunkSize) + 1;

  }

  Upload(): Promise<string> {
    return new Promise<string>(async (resolve) => {
      if (this.batchUpload.Uploaded > this.batchUpload.TotalSize){
        resolve("");
        return;
      }

      let end: number = 0;
      let chunkSize: number = 0;
      
      while(end < this.batchUpload.TotalSize){        
        end = this.batchUpload.Uploaded + this.batchUpload.ByteChunkSize;

        if (end >= this.batchUpload.TotalSize) {
          end = this.batchUpload.TotalSize;
          chunkSize = end - this.batchUpload.Uploaded;
        } else {
          chunkSize = this.batchUpload.ByteChunkSize;
        }

        let chunk = this.batchUpload.File.slice(this.batchUpload.Uploaded, end);
        let url = apiUrl + "/AddChunk?chunkSize=" + chunkSize.toFixed(0) + "&token=" + this.batchUpload.CurrentUploadToken + "&order=" + this.batchUpload.CurrentUploadCount;

        let res = await this.PostRequest(url, chunk);
  
        this.batchUpload.CurrentUploadCount++;
        this.batchUpload.Uploaded = end;

        await this.UpdateProgress();
      }

      setTimeout(() => this.batchUpload.Complete = true, 500);
      
      let customerId: number = this.cryptoService.decryptData(this.storageService.getItem('customerId'));
      let url = apiUrl + "/BatchUpload?customerId=" + customerId + "&projectId=" + this.batchUpload.ProjectId + "&fileType=" + this.batchUpload.FileType 
        + "&compressFile=" + String(this.batchUpload.CompressFile).toLowerCase()
        + "&token=" + this.batchUpload.CurrentUploadToken + "&fileName=" + this.batchUpload.File.name;
      
      let resultToken = await this.PostRequest(url, null);
      
      await this.CompleteUpload();
      resolve(resultToken);

    });
  }

  async openBatchUploadModal() {
    if(this._isModalOpen == true) return;
    this._isModalOpen = true;

    const batchUploadModal = await this.modalController.create({
      id: 'batch-upload-modal',
      component: BatchUploadModalPage,
      backdropDismiss: false,
      componentProps: {
        "batchUpload": this.batchUpload,
        "modalTitle": this.modalTitle,
        "modalDescription": this.modalDescription
      }
    });

    return await batchUploadModal.present();
  }

  closeBatchUploadModal(): Promise<void>{
    return new Promise<void>((resolve) => {
      this._isModalOpen = false;

      this.modalController.dismiss().then(() => {
        resolve();
      });
    });
  }

  GetSizeDetails(size: number): any {

    let details: any = {};
    let i: number = 0;
    let roundedSize: number = size;
    let sizeType: string = "B";

    while (roundedSize > 1024) {
        roundedSize = roundedSize / 1024;
        i++;
    }

    if (i === 1) {
        sizeType = "KB";
    } else if (i === 2) {
        sizeType = "MB";
    }
    else if (i === 3) {
        sizeType = "GB";
    }

    details["size"] = roundedSize.toFixed(2);
    details["type"] = sizeType;

    return details;
  }

  UpdateProgress(): Promise<void> {
    return new Promise<void>((resolve) => {
      let percent = (this.batchUpload.Uploaded / this.batchUpload.TotalSize) * 100;
      let uploaded = this.GetSizeDetails(this.batchUpload.Uploaded);  
      
      setTimeout(() => {
        this.batchUpload.ProgressPercent =(percent > 100 ? '100' : percent.toFixed(0)) + "%";
        this.batchUpload.ReadableUploaded = String(uploaded.size + uploaded.type);
        resolve();
      }, 500);
    });
  }
  
  CreateUploadToken(): string {
    function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    }
    return s4() + s4() + '-' + s4() + '-' + s4() + '-' +
        s4() + '-' + s4() + s4() + s4();
  }

  CompleteUpload(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.UpdateProgress();
      setTimeout(async () => {
        await this.closeBatchUploadModal();
        resolve();
      }, 1500);
    });
  }

  PostRequest(url: string, bodyData: any): Promise<string>{
    return new Promise<string>(async (resolve) => {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json'
      });
      const httpOptions = {
        headers: headers
      };      
      await this.httpClient.post<any>(url, bodyData, httpOptions).subscribe(
        data => {
          //console.log("data", data);
          resolve(data)
        },
        error => {
          //console.log("error", error.error.text)
          resolve(error.error.text)
        }
      );
    });
  }
}
